// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {CopilotChatModel, CopilotModel} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useEffect, useState} from 'react'
import {fetchCAPI, FetchCAPIError} from '../utils/fetch-capi'
import {getDisplayName} from '../utils/get-model-name'

// Logo URLs for different model vendors
const OPEN_AI_LOGO = '/images/modules/marketplace/models/families/openai-logo.svg'
const ANTHROPIC_LOGO = '/images/modules/marketplace/models/families/anthropic.svg'
const GEMINI_LOGO = '/images/modules/marketplace/models/families/gemini.svg'
const XAI_LOGO = '/images/modules/marketplace/models/families/xai.svg'
const XAI_LOGO_DARK = '/images/modules/marketplace/models/families/xai-dark.svg'

const LOGO_BY_VENDOR = new Map([
  ['Azure OpenAI', OPEN_AI_LOGO],
  ['OpenAI', OPEN_AI_LOGO],
  ['Anthropic', ANTHROPIC_LOGO],
  ['Google', GEMINI_LOGO],
  ['xAI', XAI_LOGO],
])

let cachedModels: CopilotChatModel[] | null = null
let cachePromise: Promise<CopilotChatModel[]> | null = null

export function useAgentModels() {
  const [models, setModels] = useState<CopilotChatModel[]>(cachedModels || [])
  const [isLoading, setIsLoading] = useState(!cachedModels)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (cachedModels) {
      setModels(cachedModels)
      setIsLoading(false)
      return
    }

    if (cachePromise) {
      void (async () => {
        try {
          const transformedModels = await cachePromise
          setModels(transformedModels)
          setIsLoading(false)
        } catch (err) {
          const modelsError = err instanceof ListModelsError ? err : new ListModelsError(err)
          setError(modelsError)
          setIsLoading(false)
        }
      })()
      return
    }

    async function fetchModels() {
      try {
        setIsLoading(true)
        setError(null)

        const response = await listModels()

        if (!response.ok) {
          throw new ListModelsError({status: response.status})
        }

        const data = await response.json()
        const isDarkMode = document.querySelector('[data-color-mode="dark"]') !== null
        const transformedModels = (data.data || []).map((model: CopilotModel) => {
          let logoUrl = LOGO_BY_VENDOR.get(model.vendor)
          if (model.vendor === 'xAI' && isDarkMode) {
            logoUrl = XAI_LOGO_DARK
          }

          return {
            ...model,
            displayName: getDisplayName(model.name, model.id),
            logoURL: logoUrl,
            hasLimitedCapabilities: !model.capabilities.supports.tool_calls,
            isThirdParty: model.vendor !== 'Azure OpenAI',
          }
        })

        cachedModels = transformedModels
        cachePromise = null

        return transformedModels
      } catch (err) {
        cachePromise = null
        const modelsError = err instanceof ListModelsError ? err : new ListModelsError(err)
        setError(modelsError)
        throw modelsError
      } finally {
        setIsLoading(false)
      }
    }

    if (!cachePromise) {
      cachePromise = fetchModels()
    }

    void (async () => {
      try {
        const transformedModels = await cachePromise
        setModels(transformedModels)
        setIsLoading(false)
      } catch (err) {
        const modelsError = err instanceof ListModelsError ? err : new ListModelsError(err)
        setError(modelsError)
        setIsLoading(false)
      }
    })()
  }, [])

  return {models, isLoading, error}
}

// Helper function that fetches agent specific models from the Jobs API
async function listModels(): Promise<Response> {
  const endpoint = '/agents/swe/models'

  try {
    return await fetchCAPI({
      path: endpoint,
      method: 'GET',
    })
  } catch (error) {
    // Only wrap API errors with user-friendly messages
    // For non-API errors, throw them as-is
    if (error instanceof FetchCAPIError) {
      throw new ListModelsError(error)
    }
    throw error
  }
}

// Error class for model fetching failures with user-friendly messages
export class ListModelsError extends Error {
  status?: number

  constructor(originalError: unknown) {
    // Extract status code
    let status: number | undefined
    if (
      originalError &&
      typeof originalError === 'object' &&
      'status' in originalError &&
      typeof originalError.status === 'number'
    ) {
      status = originalError.status
    }

    // Map status to user-friendly message
    let message: string

    switch (status) {
      case 0:
        message = 'Network error. Please check your connection and try again.'
        break

      case 401:
        message = "You don't have permission to view available models. Please sign in and try again."
        break

      case 500:
        message = 'Unable to load available models. Please try again later.'
        break

      default:
        message = 'An unexpected error occurred while loading models. Please try again.'
        break
    }

    super(message, {cause: originalError})
    this.name = 'ListModelsError'
    this.status = status
  }
}
